import './SignUp.css';
import { useState, useEffect } from 'react';
import LogoAnimated from '../Refs/LogoAminated.gif';
import InstanceService from '../Services/InstanceService';

const SignUp = () => {
  const [screenSize, setScreenSize] = useState('desktop');
  const [step, setStep] = useState(1); // 1: tenant name, 2: user info + instance info, 3: billing + plan
  
  // Tenant/Instance data
  const [tenantName, setTenantName] = useState('');
  const [tenantNameValid, setTenantNameValid] = useState(null); // null, true, false
  const [tenantNameChecking, setTenantNameChecking] = useState(false);
  const [tenantNameError, setTenantNameError] = useState('');
  
  const [displayName, setDisplayName] = useState('');
  const [website, setWebsite] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });
  const [billingEmails, setBillingEmails] = useState(['']);
  const [useDifferentBillingEmail, setUseDifferentBillingEmail] = useState(false);
  const [billingPhone, setBillingPhone] = useState('');
  const [useDifferentBillingPhone, setUseDifferentBillingPhone] = useState(false);
  const [trial, setTrial] = useState(false);
  
  // User data
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Responsive screen size detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Validate tenant name format
  const validateTenantNameFormat = (name) => {
    if (!name) {
      setTenantNameError('');
      return false;
    }
    
    // Check for spaces
    if (name.includes(' ')) {
      setTenantNameError('Name cannot contain spaces');
      return false;
    }
    
    // Check for special characters (only allow letters, numbers, hyphens, underscores)
    if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(name)) {
      if (/^[0-9]/.test(name)) {
        setTenantNameError('Name cannot start with a number');
      } else {
        setTenantNameError('Name can only contain letters, numbers, hyphens, and underscores');
      }
      return false;
    }
    
    setTenantNameError('');
    return true;
  };

  // Check tenant name availability with debouncing
  useEffect(() => {
    if (!tenantName || !validateTenantNameFormat(tenantName)) {
      setTenantNameValid(null);
      return;
    }

    const debounceTimer = setTimeout(() => {
      setTenantNameChecking(true);
      InstanceService.checkValidInstance(tenantName)
        .then(response => {
          // Status 200 with { valid: true, available: false } means name is taken
          // Status 204 with { valid: false, available: true } means name is available
          console.log('API Response:', response.status, response.data);
          
          if (response.status === 204 || response.data?.available === true) {
            // Name is available
            setTenantNameValid(true);
            setTenantNameError('');
          } else if (response.data?.available === false) {
            // Name is taken
            setTenantNameValid(false);
            setTenantNameError('This name is already taken');
          }
          setTenantNameChecking(false);
        })
        .catch(error => {
          console.error('Error checking tenant name:', error);
          setTenantNameChecking(false);
        });
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [tenantName]);

  const handleTenantNameChange = (e) => {
    const value = e.target.value.toLowerCase();
    setTenantName(value);
    validateTenantNameFormat(value);
  };

  const addBillingEmail = () => {
    if (billingEmails.length < 3) {
      setBillingEmails([...billingEmails, '']);
    }
  };

  const removeBillingEmail = (index) => {
    const newEmails = billingEmails.filter((_, i) => i !== index);
    setBillingEmails(newEmails.length === 0 ? [''] : newEmails);
  };

  const updateBillingEmail = (index, value) => {
    const newEmails = [...billingEmails];
    newEmails[index] = value;
    setBillingEmails(newEmails);
  };

  const validateStep1 = () => {
    return tenantNameValid === true && tenantName.length > 0;
  };

  const validateStep2 = () => {
    return firstName.trim().length > 0 &&
           lastName.trim().length > 0 &&
           email.trim().length > 0 &&
           phone.trim().length > 0 &&
           displayName.trim().length > 0;
  };

  const validateStep3 = () => {
    // If trial is selected, no billing info needed
    if (trial) {
      return true;
    }
    
    const emailsToCheck = useDifferentBillingEmail ? billingEmails : [email];
    const phoneToCheck = useDifferentBillingPhone ? billingPhone : phone;
    
    return billingAddress.street.trim().length > 0 &&
           billingAddress.city.trim().length > 0 &&
           billingAddress.state.trim().length > 0 &&
           billingAddress.zip.trim().length > 0 &&
           emailsToCheck.filter(e => e.trim().length > 0).length > 0 &&
           phoneToCheck.trim().length > 0;
  };

  const handleSubmit = async () => {
    if (!validateStep3()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    // Use user email/phone if not using different billing info
    const finalBillingEmails = useDifferentBillingEmail 
      ? billingEmails.filter(e => e.trim().length > 0) 
      : [email];
    const finalBillingPhone = useDifferentBillingPhone ? billingPhone : phone;

    const instanceData = {
      tenant: {
        name: tenantName,
        displayName: displayName,
        website: website || undefined,
        billingAddress: billingAddress,
        billingEmails: finalBillingEmails,
        billingPhone: finalBillingPhone,
        trial: trial
      },
      user: {
        firstName: firstName,
        lastName: lastName,
        email: email,
        phone: phone
      }
    };

    try {
      const response = await InstanceService.createInstance(instanceData);
      console.log('Instance created successfully:', response.data);
      // Redirect or show success message
      alert('Account created successfully!');
      window.location.href = '/console';
    } catch (err) {
      console.error('Error creating instance:', err);
      setError(err.response?.data?.message || 'Failed to create account. Please try again or contact our Support.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="signup-step">
      <h2 className={`step-title step-title-${screenSize}`}>Choose Your Tenant Name</h2>
      <p className={`step-description step-description-${screenSize}`}>
        This will be your unique identifier. Choose wisely!
      </p>
      
      <div className="form-group">
        <label className="form-label">Tenant Name</label>
        <div className="input-with-status">
          <input
            type="text"
            className={`form-input ${tenantNameValid === true ? 'valid' : tenantNameValid === false ? 'invalid' : ''}`}
            value={tenantName}
            onChange={handleTenantNameChange}
            placeholder="my-awesome-company"
          />
          {tenantNameChecking && <span className="status-indicator checking">Checking...</span>}
          {tenantNameValid === true && <span className="status-indicator valid">✓ Available</span>}
          {tenantNameValid === false && <span className="status-indicator invalid">✗ Taken</span>}
        </div>
        {tenantNameError && <span className="error-message">{tenantNameError}</span>}
        <span className="form-hint">Only letters, numbers, hyphens, and underscores. Must start with a letter.</span>
      </div>

      <button
        className={`btn-primary btn-primary-${screenSize}`}
        onClick={() => setStep(2)}
        disabled={!validateStep1()}
      >
        Continue
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="signup-step">
      <h2 className={`step-title step-title-${screenSize}`}>Your Information</h2>
      
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">First Name *</label>
          <input
            type="text"
            className="form-input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="John"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Last Name *</label>
          <input
            type="text"
            className="form-input"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Email *</label>
        <input
          type="email"
          className="form-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="john.doe@company.com"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Phone *</label>
        <input
          type="tel"
          className="form-input"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 (555) 123-4567"
        />
      </div>

      <h2 className={`step-title step-title-${screenSize}`} style={{marginTop: '3rem'}}>Instance Information</h2>
      
      <div className="form-group">
        <label className="form-label">Display Name *</label>
        <input
          type="text"
          className="form-input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="My Awesome Company"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Website (Optional)</label>
        <input
          type="url"
          className="form-input"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://mycompany.com"
        />
      </div>

      <div className="button-row">
        <button
          className={`btn-secondary btn-secondary-${screenSize}`}
          onClick={() => setStep(1)}
        >
          Back
        </button>
        <button
          className={`btn-primary btn-primary-${screenSize}`}
          onClick={() => setStep(3)}
          disabled={!validateStep2()}
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="signup-step">
      <h2 className={`step-title step-title-${screenSize}`}>Choose Your Plan</h2>
      
      <div className="plan-selection">
        <div
          className={`plan-card ${!trial ? 'selected' : ''}`}
          onClick={() => setTrial(false)}
        >
          <h3 className="plan-title">Standard Plan</h3>
          <div className="plan-price">$40/month</div>
          <div className="plan-subtitle">per agent base</div>
          <ul className="plan-features">
            <li><span className="check-icon">✓</span> Up to 5 hours of active communication included</li>
            <li><span className="check-icon">✓</span> Unlimited use at $8/hour beyond the 5 included</li>
            <li><span className="check-icon">✓</span> Full integrations access</li>
            <li><span className="check-icon">✓</span> Phone number connection</li>
            <li><span className="check-icon">✓</span> Full access online chat agent</li>

          </ul>
        </div>

        <div
          className={`plan-card ${trial ? 'selected' : ''}`}
          onClick={() => setTrial(true)}
        >
          <h3 className="plan-title">Trial Plan</h3>
          <div className="plan-price">Free</div>
          <div className="plan-subtitle">7 days</div>
          <ul className="plan-features">
            <li><span className="check-icon">✓</span> Online chat agent</li>
            <li><span className="x-icon">✗</span> No phone numbers</li>
            <li><span className="x-icon">✗</span> No integrations</li>
            <li><span className="check-icon">✓</span> Full feature access after trial</li>
          </ul>
          <div className="plan-terms">
            <strong>Terms:</strong> Only available for 7 days
          </div>
        </div>
      </div>

      {!trial && (
        <>
          <h2 className={`step-title step-title-${screenSize}`} style={{marginTop: '3rem'}}>Billing Information</h2>
          
          <div className="billing-info-section">
        <p className={`step-description step-description-${screenSize}`}>
          By default, we'll use your email ({email}) and phone ({phone}) for billing. 
          You can add additional emails or use a different phone number below.
        </p>

        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              checked={useDifferentBillingEmail}
              onChange={(e) => {
                setUseDifferentBillingEmail(e.target.checked);
                if (!e.target.checked) {
                  setBillingEmails(['']);
                }
              }}
              style={{marginRight: '0.5rem'}}
            />
            Add additional billing emails
          </label>
          
          {useDifferentBillingEmail && (
            <div style={{marginTop: '1rem'}}>
              <label className="form-label">Additional Billing Email(s) (up to 3)</label>
              {billingEmails.map((emailItem, index) => (
                <div key={index} className="email-input-row">
                  <input
                    type="email"
                    className="form-input"
                    value={emailItem}
                    onChange={(e) => updateBillingEmail(index, e.target.value)}
                    placeholder="billing@company.com"
                  />
                  {billingEmails.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeBillingEmail(index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {billingEmails.length < 3 && (
                <button
                  type="button"
                  className="btn-add"
                  onClick={addBillingEmail}
                >
                  + Add Email
                </button>
              )}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              checked={useDifferentBillingPhone}
              onChange={(e) => {
                setUseDifferentBillingPhone(e.target.checked);
                if (!e.target.checked) {
                  setBillingPhone('');
                }
              }}
              style={{marginRight: '0.5rem'}}
            />
            Use different billing phone number
          </label>
          
          {useDifferentBillingPhone && (
            <div style={{marginTop: '1rem'}}>
              <input
                type="tel"
                className="form-input"
                value={billingPhone}
                onChange={(e) => setBillingPhone(e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </div>
          )}
        </div>
      </div>

      <div className="form-section">
        <h3 className={`section-title section-title-${screenSize}`}>Billing Address</h3>
        
        <div className="form-group">
          <label className="form-label">Street Address *</label>
          <input
            type="text"
            className="form-input"
            value={billingAddress.street}
            onChange={(e) => setBillingAddress({...billingAddress, street: e.target.value})}
            placeholder="123 Main Street"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Street Address 2 (Optional)</label>
          <input
            type="text"
            className="form-input"
            value={billingAddress.street2}
            onChange={(e) => setBillingAddress({...billingAddress, street2: e.target.value})}
            placeholder="Suite 100"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">City *</label>
            <input
              type="text"
              className="form-input"
              value={billingAddress.city}
              onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
              placeholder="New York"
            />
          </div>

          <div className="form-group">
            <label className="form-label">State *</label>
            <input
              type="text"
              className="form-input"
              value={billingAddress.state}
              onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
              placeholder="NY"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">ZIP Code *</label>
            <input
              type="text"
              className="form-input"
              value={billingAddress.zip}
              onChange={(e) => setBillingAddress({...billingAddress, zip: e.target.value})}
              placeholder="10001"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Country *</label>
            <select
              className="form-input form-select"
              value={billingAddress.country}
              onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value})}
            >
              <option value="USA">USA</option>
              <option value="CAN">Canada</option>
              <option value="MEX">Mexico</option>
            </select>
          </div>
        </div>
      </div>
        </>
      )}

      {error && <div className="error-banner">{error}</div>}

      <div className="button-row">
        <button
          className={`btn-secondary btn-secondary-${screenSize}`}
          onClick={() => setStep(2)}
          disabled={loading}
        >
          Back
        </button>
        <button
          className={`btn-primary btn-primary-${screenSize}`}
          onClick={handleSubmit}
          disabled={!validateStep3() || loading}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </div>
    </div>
  );

  return (
    <div className={`signup-page signup-${screenSize}`}>
      <header className={`signup-header signup-header-${screenSize}`}>
        <div className="header-content">
          <div className="logo">
            <img src={LogoAnimated} alt="Sofie AI Logo" className={`logo-animated-${screenSize}`} />
            <div className='logo-text-container'>
              <div className={`logo-text-line-1 logo-text-line-1-${screenSize}`}>Sofie</div>
              <div className={`logo-text-line-2 logo-text-line-2-${screenSize}`}>AI Agent</div>
            </div>
          </div>
        </div>
      </header>

      <div className={`signup-content signup-content-${screenSize}`}>
        <div className={`signup-container signup-container-${screenSize}`}>
          <div className="progress-indicator">
            <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
            <div className={`progress-line ${step >= 2 ? 'active' : ''}`}></div>
            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
            <div className={`progress-line ${step >= 3 ? 'active' : ''}`}></div>
            <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3</div>
          </div>

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>
    </div>
  );
};

export default SignUp;